import React from "react"
import styles from "./PdfToolbar.module.css"

const textSizeOptions = [12, 16, 20, 24]
const textColorOptions = [
  { label: "Red", value: "#d32f2f" },
  { label: "Black", value: "#212121" },
  { label: "Blue", value: "#1565c0" },
  { label: "Green", value: "#2e7d32" },
  { label: "Purple", value: "#6a1b9a" }
]
const textWeightOptions = [
  { label: "Normal", value: "normal" },
  { label: "Bold", value: "bold" }
]

const drawThicknessOptions = [
  { label: "Thin", value: 1 },
  { label: "Medium", value: 2 },
  { label: "Thick", value: 3 },
  { label: "Heavy", value: 4 }
]
const drawColorOptions = [
  { label: "Black", value: "#212121" },
  { label: "Blue", value: "#0d47a1" },
  { label: "Red", value: "#c62828" }
]
const drawStyleOptions = [
  { label: "Smooth", value: "smooth" },
  { label: "Sharp", value: "sharp" }
]

const highlightColorOptions = [
  { label: "Yellow", value: "#fff176" },
  { label: "Green", value: "#aed581" },
  { label: "Pink", value: "#f48fb1" },
  { label: "Blue", value: "#81d4fa" },
  { label: "Orange", value: "#ffcc80" }
]
const highlightOpacityOptions = [
  { label: "Light", value: 0.3 },
  { label: "Medium", value: 0.5 },
  { label: "Heavy", value: 0.7 }
]

const shapeStrokeWidthOptions = [1, 2, 3, 4, 6, 8]
const shapeStrokeColorOptions = [
  { label: "Red", value: "#c62828" },
  { label: "Blue", value: "#1565c0" },
  { label: "Green", value: "#2e7d32" },
  { label: "Black", value: "#212121" },
  { label: "Orange", value: "#ef6c00" }
]
const shapeFillColorOptions = [
  { label: "Red", value: "#c62828" },
  { label: "Blue", value: "#1565c0" },
  { label: "Green", value: "#2e7d32" },
  { label: "Purple", value: "#6a1b9a" },
  { label: "Yellow", value: "#ffd600" }
]
const shapeFillOpacityOptions = [
  { label: "Light", value: 0.2 },
  { label: "Medium", value: 0.4 },
  { label: "Heavy", value: 0.6 }
]

const shapeTools = ["line", "rectangle", "circle", "ellipse"]

export default function ToolOptionsPanel({
  activeTool,
  settings,
  onSettingChange
}) {
  if (!settings) return null

  const handleUpdate = (toolKey, updates) => {
    if (typeof onSettingChange === "function") {
      onSettingChange(toolKey, updates)
    }
  }

  if (!["text", "draw", "highlight", ...shapeTools].includes(activeTool)) {
    return null
  }

  const textSettings = settings.text || {}
  const drawSettings = settings.draw || {}
  const highlightSettings = settings.highlight || {}
  const activeShapeSettings = shapeTools.includes(activeTool)
    ? settings[activeTool] || {}
    : {}

  return (
    <div className={styles.optionsContainer}>
      {activeTool === "text" && (
        <>
          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Font Size</span>
            <div className={styles.optionControls}>
              {textSizeOptions.map(size => (
                <button
                  key={size}
                  type="button"
                  className={`${styles.optionButton} ${
                    textSettings.fontSize === size ? styles.optionButtonActive : ""
                  }`}
                  onClick={() => handleUpdate("text", { fontSize: size })}
                  aria-label={`Set font size to ${size}px`}>
                  {size}
                  px
                </button>
              ))}
            </div>
          </div>

          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Color</span>
            <div className={styles.optionControls}>
              {textColorOptions.map(color => (
                <button
                  key={color.value}
                  type="button"
                  className={`${styles.colorSwatchButton} ${
                    textSettings.color === color.value ? styles.colorSwatchActive : ""
                  }`}
                  onClick={() => handleUpdate("text", { color: color.value })}
                  aria-label={`Set text color to ${color.label}`}
                  style={{ backgroundColor: color.value }} />
              ))}
            </div>
          </div>

          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Weight</span>
            <div className={styles.optionControls}>
              {textWeightOptions.map(weight => (
                <button
                  key={weight.value}
                  type="button"
                  className={`${styles.optionButton} ${
                    textSettings.fontWeight === weight.value ? styles.optionButtonActive : ""
                  }`}
                  onClick={() => handleUpdate("text", { fontWeight: weight.value })}>
                  {weight.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTool === "draw" && (
        <>
          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Thickness</span>
            <div className={styles.optionControls}>
              {drawThicknessOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.optionButton} ${
                    drawSettings.lineWidth === option.value ? styles.optionButtonActive : ""
                  }`}
                  onClick={() => handleUpdate("draw", { lineWidth: option.value })}>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Color</span>
            <div className={styles.optionControls}>
              {drawColorOptions.map(color => (
                <button
                  key={color.value}
                  type="button"
                  className={`${styles.colorSwatchButton} ${
                    drawSettings.color === color.value ? styles.colorSwatchActive : ""
                  }`}
                  onClick={() => handleUpdate("draw", { color: color.value })}
                  aria-label={`Set signature color to ${color.label}`}
                  style={{ backgroundColor: color.value }} />
              ))}
            </div>
          </div>

          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Style</span>
            <div className={styles.optionControls}>
              {drawStyleOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.optionButton} ${
                    drawSettings.lineStyle === option.value ? styles.optionButtonActive : ""
                  }`}
                  onClick={() => handleUpdate("draw", { lineStyle: option.value })}>
                  {option.label}
                </button>
              ))}
              <div className={styles.previewTile}>
                <div
                  className={styles.previewSwatch}
                  style={{
                    background: drawSettings.color || "#0d47a1"
                  }} />
                <span>
                  {drawSettings.lineWidth || 2}
                  px
                  {" "}
                  {drawSettings.lineStyle === "sharp" ? "sharp" : "smooth"}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTool === "highlight" && (
        <>
          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Color</span>
            <div className={styles.optionControls}>
              {highlightColorOptions.map(color => (
                <button
                  key={color.value}
                  type="button"
                  className={`${styles.colorSwatchButton} ${
                    highlightSettings.color === color.value ? styles.colorSwatchActive : ""
                  }`}
                  onClick={() => handleUpdate("highlight", { color: color.value })}
                  aria-label={`Set highlight color to ${color.label}`}
                  style={{ backgroundColor: color.value }} />
              ))}
            </div>
          </div>

          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Opacity</span>
            <div className={styles.optionControls}>
              {highlightOpacityOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.optionButton} ${styles.opacityChip} ${
                    highlightSettings.opacity === option.value ? styles.optionButtonActive : ""
                  }`}
                  onClick={() => handleUpdate("highlight", { opacity: option.value })}>
                  {Math.round(option.value * 100)}
                  %
                </button>
              ))}
              <div className={styles.previewTile}>
                <div
                  className={styles.previewSwatch}
                  style={{
                    background: highlightSettings.color || "#fff176",
                    opacity: highlightSettings.opacity || 0.5
                  }} />
                <span>
                  {Math.round((highlightSettings.opacity || 0.5) * 100)}
                  %
                  opacity
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {shapeTools.includes(activeTool) && (
        <>
          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Stroke Width</span>
            <div className={styles.optionControls}>
              {shapeStrokeWidthOptions.map(value => (
                <button
                  key={value}
                  type="button"
                  className={`${styles.optionButton} ${
                    activeShapeSettings.strokeWidth === value ? styles.optionButtonActive : ""
                  }`}
                  onClick={() => handleUpdate(activeTool, { strokeWidth: value })}
                  aria-label={`Set stroke width to ${value}px`}>
                  {value}
                  px
                </button>
              ))}
            </div>
          </div>

          <div className={styles.optionSection}>
            <span className={styles.optionTitle}>Stroke Color</span>
            <div className={styles.optionControls}>
              {shapeStrokeColorOptions.map(color => (
                <button
                  key={color.value}
                  type="button"
                  className={`${styles.colorSwatchButton} ${
                    activeShapeSettings.strokeColor === color.value ? styles.colorSwatchActive : ""
                  }`}
                  onClick={() => handleUpdate(activeTool, { strokeColor: color.value })}
                  aria-label={`Set stroke color to ${color.label}`}
                  style={{ backgroundColor: color.value }} />
              ))}
            </div>
          </div>

          {activeTool !== "line" && (
            <>
              <div className={styles.optionSection}>
                <span className={styles.optionTitle}>Fill</span>
                <div className={styles.optionControls}>
                  <button
                    type="button"
                    className={`${styles.optionButton} ${activeShapeSettings.fillEnabled ? styles.optionButtonActive : ""}`}
                    onClick={() => handleUpdate(activeTool, { fillEnabled: !activeShapeSettings.fillEnabled })}>
                    {activeShapeSettings.fillEnabled ? "Enabled" : "Disabled"}
                  </button>
                  {activeShapeSettings.fillEnabled && (
                    <div className={styles.optionControls}>
                      {shapeFillColorOptions.map(color => (
                        <button
                          key={color.value}
                          type="button"
                          className={`${styles.colorSwatchButton} ${
                            activeShapeSettings.fillColor === color.value ? styles.colorSwatchActive : ""
                          }`}
                          onClick={() => handleUpdate(activeTool, { fillColor: color.value })}
                          aria-label={`Set fill color to ${color.label}`}
                          style={{ backgroundColor: color.value }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {activeShapeSettings.fillEnabled && (
                <div className={styles.optionSection}>
                  <span className={styles.optionTitle}>Fill Opacity</span>
                  <div className={styles.optionControls}>
                    {shapeFillOpacityOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`${styles.optionButton} ${
                          activeShapeSettings.fillOpacity === option.value ? styles.optionButtonActive : ""
                        }`}
                        onClick={() => handleUpdate(activeTool, { fillOpacity: option.value })}>
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
